import { HttpClient, HttpHeaders } from "@angular/common/http";
import { throwError, Observable, BehaviorSubject, of } from "rxjs";

import { Injectable } from "@angular/core";
import { tap, catchError, finalize, switchMap, filter, take } from 'rxjs/operators';
import { Router } from "@angular/router";
import { environment } from "../../environments/environment";

interface TokenResp {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  permittedMenu: any;

  private domain = environment.apiUrl;
  public apiurl;
  headers = new HttpHeaders()
    .set("Content-Type", "application/json")
    .set("Accept", "application/json");
  httpOptions = { headers: this.headers };
  activeurl: any;
  private refreshInProgress = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);
  private refreshTimerId: any = null;

  private readonly REFRESH_BEFORE_EXPIRY_SECONDS = 60; 

  constructor(private http: HttpClient, private route: Router) {
  }

  private handleError(error: any) {
    return throwError(error);
  }

  loginUser(user): Observable<any> {
    this.permittedMenu = []
    this.ApiPemissionMenus();
    this.apiurl = this.domain + `api/login`;
    return this.http
      .post<any>(this.apiurl, user)
      .pipe(tap(res => this.setTokens(res.data)));
  }

  refresh(refreshToken: string) {
    return this.http.post<any>(`${this.domain}api/refresh`, { refresh_token: refreshToken }).pipe(
      tap(res => {
        const payload = res?.data ?? res;
        if (payload && (payload.access_token || payload.expires_in)) {
          const tokenResp: TokenResp = {
            access_token: payload.access_token ?? payload.accessToken,
            refresh_token: payload.refresh_token ?? payload.refreshToken,
            expires_in: payload.expires_in
              ?? payload.expiresIn
              ?? payload.expires
              ?? 0
          };
          this.setTokens(tokenResp);
        }
      })
    );
  }

  logout() {
    const token = this.getAccessToken();
    return this.http.post(`${this.domain}api/logout`, {}).pipe(tap(() => this.clearTokens()));
  }

  setTokens(resp: TokenResp) {
    const expiresAt = Date.now() + (resp.expires_in || 0) * 1000; 
    localStorage.setItem('access_token', resp.access_token);
    localStorage.setItem('token', resp.access_token);
    if (resp.refresh_token) {
      localStorage.setItem('refresh_token', resp.refresh_token);
    }
    localStorage.setItem('expires_at', String(expiresAt));

    this.refreshSubject.next(resp.access_token);

    this.scheduleRefreshFromExpiry();
  }

  getAccessToken(): string | null {
    return localStorage.getItem('access_token');
  }

  getAccessTokenExpiry(): number | null {
    const exp = localStorage.getItem('expires_at');
    return exp ? Number(exp) : null;
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  clearTokens() {
    localStorage.removeItem('token')
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('token');
    this.clearRefreshTimer();
    this.refreshSubject.next(null);
  }

  isAccessTokenExpired(): boolean {
    const exp = localStorage.getItem('expires_at');
    if (!exp) return true;
    return Date.now() > Number(exp);
  }

  passwordChange(user: any, type: any): Observable<any> {
    this.apiurl = this.domain + type;
    return this.http
      .post<any>(this.apiurl, user)
      .pipe(tap(), catchError(this.handleError));
  }

  loggedIn() {
    return !!localStorage.getItem('token');
  }
  adminLoggedIn() {
    return !!localStorage.getItem('token') && localStorage.getItem('user_type') === 'admin'
  }
  firstLogin() {
    return !!localStorage.getItem('is_first_login');
  }

  employeeLoggedIn() {
    return !!localStorage.getItem('token') && localStorage.getItem('user_type') === 'employee'
  }
  clientLoggedIn() {
    return !!localStorage.getItem('token') && localStorage.getItem('user_type') === 'client'
  }
  companyLoggedIn() {
    return !!localStorage.getItem('token') && localStorage.getItem('user_type') === 'company'
  }

  getToken() {
    return localStorage.getItem('token')
  }

  logoutUser() {
    this.clearTokens();
    this.route.navigate(['/login'])
  }

  async checkPermission(): Promise<boolean> {
    const userType = localStorage.getItem('user_type');
    const permittedMenu = this.permittedMenu?.data?.menus ?? [];
    localStorage.setItem('is_create', 'no');
    localStorage.setItem('is_modify', 'no');
    localStorage.setItem('is_remove', 'no');
    localStorage.setItem('menu_key', '');
    const menu: any = permittedMenu.filter((val: any) => ('/' + userType + '/' + val.menu_link) == this.activeurl);
    if (menu.length != 0) {
      localStorage.setItem('is_create', menu[0].is_create == 1 ? 'yes' : 'no');
      localStorage.setItem('is_modify', menu[0].is_modify == 1 ? 'yes' : 'no');
      localStorage.setItem('is_remove', menu[0].is_remove == 1 ? 'yes' : 'no');
      localStorage.setItem('menu_key', menu[0].menu_key != null ? menu[0].menu_key : '');

      if (menu[0].is_remove == 1 || menu[0].is_read == 1 || menu[0].is_modify == 1 || menu[0].is_create == 1) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  async PemissionMenus(activeurl: string): Promise<void> {
    this.activeurl = activeurl;
    await this.ApiPemissionMenus();
  }

  ApiPemissionMenus(): Promise<void> {
    this.apiurl = `${this.domain}api/permission/menu`;
    const token = localStorage.getItem('token') || '';
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Accept', 'application/json')
      .set('Authorization', 'Bearer ' + token);
    const httpOptionsToken = { headers };

    return this.http.get<any>(this.apiurl, httpOptionsToken).toPromise()
      .then(permittedMenu => {
        this.permittedMenu = permittedMenu;
      })
      .catch(error => {
        console.error(error);
      });
  }

  startup(): void {
    const access = this.getAccessToken();
    const exp = localStorage.getItem('expires_at');
    if (access && exp) {
      this.scheduleRefreshFromExpiry();
    }
  }


  forceRefresh(): Observable<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.clearTokens();
      this.route.navigate(['/login']);
      return throwError(() => new Error('No refresh token'));
    }

    if (this.refreshInProgress) {
      return this.refreshSubject.pipe(
        filter((t) => t != null),
        take(1),
        switchMap((token) => of(token!))
      );
    }

    this.refreshInProgress = true;
    this.refreshSubject.next(null);

    return this.refresh(refreshToken).pipe(
      switchMap((res: any) => {
        const payload = res?.data ?? res;
        const newAccess = payload?.access_token ?? payload?.accessToken ?? this.getAccessToken();
        this.refreshSubject.next(newAccess ?? null);
        return of(newAccess ?? '');
      }),
      catchError((err) => {
        this.clearTokens();
        this.route.navigate(['/login']);
        return throwError(() => err);
      }),
      finalize(() => {
        this.refreshInProgress = false;
      })
    );
  }

  private scheduleRefreshFromExpiry() {
    this.clearRefreshTimer();

    const expStr = localStorage.getItem('expires_at');
    if (!expStr) return;

    const expiryMs = Number(expStr);
    const nowMs = Date.now();
    const refreshBeforeMs = this.REFRESH_BEFORE_EXPIRY_SECONDS * 1000;
    let delayMs = expiryMs - nowMs - refreshBeforeMs;

    if (delayMs <= 0) {
      this.forceRefresh().subscribe({
        next: () => {  },
        error: () => { }
      });
      return;
    }

    const MAX_TIMEOUT = 2147483647;
    if (delayMs > MAX_TIMEOUT) {
      delayMs = MAX_TIMEOUT;
    }

    this.refreshTimerId = setTimeout(() => {
      this.forceRefresh().subscribe({
        next: () => { },
        error: () => { }
      });
    }, delayMs);
  }

  private clearRefreshTimer() {
    if (this.refreshTimerId) {
      clearTimeout(this.refreshTimerId);
      this.refreshTimerId = null;
    }
  }
}
